<!-- SOLAPA «ESTADO»: interruptor general, claves por proveedor, modelo por omisión y tope diario.
     Ruta hija de `console/ia`: el encabezado y las solapas los pone el padre. -->
<div class="space-y-8 pt-2">

  <!-- ── Cargando ── -->
  <div *ngIf="cargando" class="space-y-4" aria-live="polite" aria-busy="true">
    <div class="kb-skeleton h-24 w-full rounded-xl"></div>
    <div class="kb-stat-grid"><div class="kb-skeleton h-24 w-full rounded-xl" *ngFor="let s of [1,2,3]"></div></div>
    <div class="kb-skeleton h-48 w-full rounded-xl"></div>
  </div>

  <!-- ── No se pudo leer el estado ── -->
  <div class="kb-tira" *ngIf="!cargando && error" role="alert">
    <p class="m-0 text-sm">
      <i class="fas fa-exclamation-circle mr-1.5 text-destructive" aria-hidden="true"></i>
      No se pudo leer el estado de la IA. <span class="text-muted-foreground">{{ error }}</span>
    </p>
    <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" (click)="cargar()">
      <i class="fas fa-redo !text-foreground" aria-hidden="true"></i>Reintentar
    </button>
  </div>

  <ng-container *ngIf="!cargando && estado">

    <!-- ══ EL INTERRUPTOR GENERAL ══════════════════════════════════════════════
         Apagada: la tarjeta lo dice en rojo y ofrece prender, sin preguntar nada.
         Prendida: el botón rojo abre la única confirmación de la consola que es un
         diálogo, porque interrumpir ES su función. -->
    <section class="kb-tarjeta" [class.!border-destructive]="apagada">
      <div class="kb-tarjeta-cab">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
              <i class="fas fa-power-off text-muted-foreground" [class.!text-destructive]="apagada" aria-hidden="true"></i>
              {{ apagada ? 'La IA está apagada' : 'La IA está prendida' }}
            </h2>
            <p class="m-0 mt-2 text-sm text-muted-foreground" *ngIf="!apagada && puedeEditar">
              Corta de una vez a <span class="font-medium text-foreground">todos</span> los agentes: nadie llama a
              ningún modelo y no se gasta nada. Para un incidente, o para frenar un gasto que se fue de las manos.
            </p>
            <p class="m-0 mt-2 text-sm text-muted-foreground" *ngIf="!apagada && !puedeEditar">
              Los agentes pueden contestar.
            </p>
            <p class="m-0 mt-2 text-sm text-muted-foreground" *ngIf="apagada">
              <ng-container *ngIf="estado.apagado.causa">{{ nombreCausa(estado.apagado.causa) }} · </ng-container>
              <ng-container *ngIf="estado.apagado.motivo">«{{ estado.apagado.motivo }}»</ng-container>
              <ng-container *ngIf="!estado.apagado.motivo">Sin motivo anotado</ng-container>
              <ng-container *ngIf="estado.apagado.por"> · la apagó {{ estado.apagado.por }}</ng-container>
              <ng-container *ngIf="estado.apagado.desde"> · {{ haceCuanto(estado.apagado.desde) }}</ng-container>
              <br>Ningún comercio ve el asistente mientras tanto, aunque lo tenga prendido.
            </p>
          </div>
          <div class="shrink-0" *ngIf="puedeEditar">
            <button type="button" class="kb-btn kb-btn-danger" *ngIf="!apagada" (click)="abrirApagar()">
              <i class="fas fa-power-off" aria-hidden="true"></i>Apagar toda la IA
            </button>
            <button type="button" class="kb-btn kb-btn-primary" *ngIf="apagada"
                    [disabled]="cambiandoApagado" (click)="prender()">
              <i class="fas" [ngClass]="cambiandoApagado ? 'fa-circle-notch fa-spin' : 'fa-play'" aria-hidden="true"></i>Volver a prender
            </button>
          </div>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo" *ngIf="errorApagado && !mostrarApagar">
        <p class="kb-error m-0" role="alert">{{ errorApagado }}</p>
      </div>
    </section>

    <!-- ══ LAS TRES CIFRAS ═══════════════════════════════════════════════════ -->
    <div class="kb-stat-grid xl:grid-cols-3">
      <div class="kb-stat">
        <span class="kb-stat-label block">Gasto de hoy</span>
        <span class="kb-stat-valor block">{{ usd(estado.gastoHoyUsd) }}</span>
        <div class="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted" role="progressbar"
             [attr.aria-valuenow]="fraccion * 100" aria-valuemin="0" aria-valuemax="100"
             aria-label="Gasto de hoy contra el tope diario">
          <div class="h-full rounded-full bg-foreground" [class.!bg-destructive]="fraccion >= 1"
               [style.width.%]="fraccion * 100"></div>
        </div>
        <span class="kb-stat-pie block">
          <ng-container *ngIf="fraccion < 1">de {{ usd(estado.topeDiarioUsd) }} · quedan {{ usd(queda) }} para hoy</ng-container>
          <ng-container *ngIf="fraccion >= 1">Se llegó al tope de {{ usd(estado.topeDiarioUsd) }}: lo que sigue queda como Omitido, que no es una falla.</ng-container>
        </span>
      </div>
      <div class="kb-stat">
        <span class="kb-stat-label block">Modelo por omisión</span>
        <!-- Sin negrita: no es una cifra (regla de Juan: sólo la cifra grande va gruesa). -->
        <span class="kb-stat-valor block !text-lg !font-normal">{{ nombreModelo(estado.modeloPorOmision) }}</span>
        <span class="kb-stat-pie block font-mono">{{ estado.modeloPorOmision || '—' }}</span>
      </div>
      <div class="kb-stat">
        <span class="kb-stat-label block">Clave maestra del servidor</span>
        <span class="kb-stat-valor block !text-lg !font-normal">{{ estado.claveMaestra ? 'Sí' : 'No' }}</span>
        <span class="kb-stat-pie block">
          {{ estado.claveMaestra
             ? 'Las claves que se cargan aquí se guardan cifradas.'
             : 'Sin ella no se pueden guardar claves desde esta consola.' }}
        </span>
      </div>
    </div>

    <!-- ══ LAS CLAVES ════════════════════════════════════════════════════════ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-key text-muted-foreground" aria-hidden="true"></i>Claves de los proveedores
          </h2>
          <p class="m-0 mt-2 text-sm text-muted-foreground">
            Son de CrediTotal. Un comercio puede tener la suya desde su ficha, en «Asistente de IA».
          </p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <app-ia-credenciales [estado]="estado" [puedeEditar]="puedeEditar"
                             (cambio)="releerEstado()"></app-ia-credenciales>
      </div>
    </section>

    <!-- ══ MODELO Y TOPE ═════════════════════════════════════════════════════ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <div>
          <h2 class="flex items-center gap-2 text-base font-semibold leading-none">
            <i class="fas fa-sliders-h text-muted-foreground" aria-hidden="true"></i>Modelo y tope diario
          </h2>
          <p class="m-0 mt-2 text-sm text-muted-foreground">
            El modelo que usa un agente cuando no tiene uno propio, y cuánto se puede gastar entre todos en un día.
          </p>
        </div>
      </div>
      <div class="kb-tarjeta-cuerpo">
        <form class="grid gap-4 sm:grid-cols-2" (ngSubmit)="guardarAjustes()">
          <div>
            <label class="kb-label" for="ia-modelo">Modelo por omisión</label>
            <div class="kb-select-envoltorio">
              <select id="ia-modelo" name="modelo" class="kb-select" [(ngModel)]="modelo"
                      [disabled]="!puedeEditar || !catalogo.length">
                <option *ngIf="modeloFueraDeCatalogo" [value]="modelo">{{ modelo }}</option>
                <option *ngFor="let m of catalogo" [value]="m.id">{{ m.nombre }} · {{ precio(m) }}</option>
              </select>
              <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
            </div>
            <p class="kb-hint" *ngIf="errorCatalogo">No se pudo leer el catálogo de modelos: {{ errorCatalogo }}</p>
            <p class="kb-hint" *ngIf="!errorCatalogo">Sólo los modelos del catálogo del servidor.</p>
          </div>
          <div>
            <label class="kb-label" for="ia-tope">Tope diario, en dólares</label>
            <input id="ia-tope" name="tope" class="kb-input" type="number" min="0" step="0.01"
                   inputmode="decimal" [(ngModel)]="tope" [disabled]="!puedeEditar"
                   [class.kb-input-invalido]="tope !== '' && !topeValido">
            <p class="kb-hint">Hoy van {{ usd(estado.gastoHoyUsd) }}. Al llegar al tope, los agentes dejan de llamar al modelo hasta mañana.</p>
          </div>
          <div class="flex flex-wrap items-center gap-3 sm:col-span-2" *ngIf="puedeEditar">
            <button type="submit" class="kb-btn kb-btn-primary"
                    [disabled]="guardandoAjustes || !ajustesCambiados || !topeValido">
              <i class="fas" [ngClass]="guardandoAjustes ? 'fa-circle-notch fa-spin' : 'fa-save'" aria-hidden="true"></i>Guardar el modelo y el tope
            </button>
            <p class="m-0 text-sm" *ngIf="resultadoAjustes" role="status"
               [ngClass]="resultadoAjustes.ok ? 'kb-texto-exito' : 'kb-texto-peligro'">{{ resultadoAjustes.texto }}</p>
          </div>
        </form>
      </div>
    </section>
  </ng-container>
</div>

<!-- ══ CONFIRMAR EL APAGADO — la única excepción a «nada de modales» ═══════════
     Pide la causa, el motivo (queda anotado) y escribir APAGAR. -->
<div class="kb-modal-fondo" *ngIf="mostrarApagar" (click)="cerrarApagar()" (keydown.escape)="cerrarApagar()">
  <div class="kb-modal" role="dialog" aria-modal="true" aria-labelledby="ia-apagar-titulo"
       (click)="$event.stopPropagation()">
    <div class="kb-modal-header">
      <div>
        <h2 id="ia-apagar-titulo" class="kb-modal-titulo">Vas a dejar a todos los agentes sin correr</h2>
        <p class="kb-modal-desc">
          Ningún comercio va a poder preguntarle al asistente y el botón desaparece de su portal.
          Tarda hasta 10 segundos en tomar efecto.
        </p>
      </div>
      <button type="button" class="kb-modal-cerrar" aria-label="Cerrar" (click)="cerrarApagar()">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
    <form (ngSubmit)="apagar()">
      <div class="kb-modal-body space-y-4">
        <div>
          <label class="kb-label" for="ia-causa">Causa</label>
          <div class="kb-select-envoltorio">
            <select id="ia-causa" name="causa" class="kb-select" [(ngModel)]="causa">
              <option *ngFor="let c of CAUSAS" [value]="c.id">{{ c.nombre }}</option>
            </select>
            <i class="fas fa-chevron-down kb-select-flecha" aria-hidden="true"></i>
          </div>
        </div>
        <div>
          <label class="kb-label" for="ia-motivo">Por qué (queda anotado)</label>
          <textarea id="ia-motivo" name="motivo" class="kb-textarea" rows="2" [(ngModel)]="motivo"
                    placeholder="Se disparó el gasto, lo reviso mañana"></textarea>
        </div>
        <div>
          <label class="kb-label" for="ia-palabra">
            Escribe <span class="font-mono font-semibold">APAGAR</span> para confirmar
          </label>
          <input id="ia-palabra" name="palabra" class="kb-input font-mono" autocomplete="off"
                 spellcheck="false" [(ngModel)]="palabra">
        </div>
        <p class="kb-error m-0" *ngIf="errorApagado" role="alert">{{ errorApagado }}</p>
      </div>
      <div class="kb-modal-footer">
        <button type="button" class="kb-btn kb-btn-outline" (click)="cerrarApagar()">Mejor no</button>
        <button type="submit" class="kb-btn kb-btn-danger" [disabled]="!puedeConfirmarApagar">
          <i class="fas" [ngClass]="cambiandoApagado ? 'fa-circle-notch fa-spin' : 'fa-power-off'" aria-hidden="true"></i>Apagar toda la IA
        </button>
      </div>
    </form>
  </div>
</div>
